/**
 * VehicleLocationCard Component
 *
 * Displays current vehicle location on a work order
 * with option to change it and view location history.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Button,
  Box,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditLocationIcon from '@mui/icons-material/EditLocation';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { format } from 'date-fns';
import { useVehicleLocationHistory } from '@/hooks/useWorkOrders';
import { VehicleLocationDialog } from './VehicleLocationDialog';
import { dateFnsLocale } from '@/utils/locale';

interface VehicleLocationCardProps {
  workOrderId: string;
  currentLocation: string | null;
  currentLocationDetail: string | null;
  cardVariant?: 'elevation' | 'outlined';
}

const LOCATION_COLORS: Record<string, 'default' | 'info' | 'primary' | 'success' | 'secondary'> = {
  arrival: 'info',
  on_lift: 'primary',
  parking: 'default',
  ready: 'success',
  delivered: 'secondary',
};

const DOT_COLOR_MAP: Record<string, string> = {
  arrival: 'info.main',
  on_lift: 'primary.main',
  parking: 'text.secondary',
  ready: 'success.main',
  delivered: 'secondary.main',
};

export function VehicleLocationCard({
  workOrderId,
  currentLocation,
  currentLocationDetail,
  cardVariant = 'outlined',
}: VehicleLocationCardProps) {
  const { t } = useTranslation('workorders');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const { data: history } = useVehicleLocationHistory(showHistory ? workOrderId : undefined);

  const locationLabelKey = currentLocation
    ? `vehicleLocation.${currentLocation}` as const
    : null;

  return (
    <>
      <Card variant={cardVariant}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LocationOnIcon color="action" fontSize="small" />
              <Typography variant="subtitle2">{t('vehicleLocation.title')}</Typography>
            </Stack>
            <Button
              size="small"
              startIcon={<EditLocationIcon />}
              onClick={() => setDialogOpen(true)}
            >
              {t('vehicleLocation.changeLocation')}
            </Button>
          </Stack>

          {currentLocation ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                label={t(locationLabelKey!)}
                color={LOCATION_COLORS[currentLocation] ?? 'default'}
                size="small"
              />
              {currentLocationDetail && (
                <Typography variant="body2" color="text.secondary">
                  {currentLocationDetail}
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          )}

          <Button
            size="small"
            sx={{ mt: 1, p: 0 }}
            onClick={() => setShowHistory((prev) => !prev)}
          >
            {showHistory ? t('vehicleLocation.title') : t('vehicleLocation.history')}
          </Button>

          {showHistory && history && history.length > 0 && (
            <Stack sx={{ mt: 1, pl: 1 }} spacing={0}>
              {history.map((entry, idx) => (
                <Stack key={entry.id} direction="row" spacing={1.5} alignItems="flex-start">
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                    <FiberManualRecordIcon
                      sx={{
                        fontSize: 12,
                        color: DOT_COLOR_MAP[entry.locationType] ?? 'text.disabled',
                      }}
                    />
                    {idx < history.length - 1 && (
                      <Box
                        sx={{
                          width: 2,
                          flexGrow: 1,
                          minHeight: 24,
                          bgcolor: 'divider',
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ pb: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {t(`vehicleLocation.${entry.locationType}` as const)}
                    </Typography>
                    {entry.locationDetail && (
                      <Typography variant="caption" color="text.secondary">
                        {entry.locationDetail}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {format(new Date(entry.recordedAt), 'dd.MM.yyyy HH:mm', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          )}

          {showHistory && history && history.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('vehicleLocation.noHistory')}
            </Typography>
          )}
        </CardContent>
      </Card>

      <VehicleLocationDialog
        open={dialogOpen}
        workOrderId={workOrderId}
        onClose={() => setDialogOpen(false)}
      />
    </>
  );
}
